import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { ASTERISK_API_BASE } from '../config/bridge';
import { resolveSession } from '../lib/session';
import { useSessionUser } from '../SessionContext';
import './Reports.css';

const API_LIMIT = 500;
const PAGE_SIZE = 50;
const REPORT_PERIODS = [
  { key: 'daily', label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'interval', label: 'Interval' }
];

const text = (value) => String(value ?? '').trim();

const read = (source, keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) return source[key];
  }
  return '';
};

const toIsoDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const todayIso = () => toIsoDate(new Date());

const monthStartIso = () => {
  const now = new Date();
  return toIsoDate(new Date(now.getFullYear(), now.getMonth(), 1));
};

const currentMonthIso = () => todayIso().slice(0, 7);

const addDays = (dateValue, days) => {
  const date = new Date(`${dateValue}T12:00:00`);
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
};

const weekStartIso = (dateValue) => {
  const date = new Date(`${dateValue}T12:00:00`);
  date.setDate(date.getDate() - date.getDay());
  return toIsoDate(date);
};

const monthEndIso = (monthValue) => {
  const [year, month] = String(monthValue || '').split('-').map(Number);
  if (!year || !month) return '';
  return toIsoDate(new Date(year, month, 0));
};

const karachiTimestamp = (value, endOfMinute = false) => {
  const normalized = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(normalized)) return NaN;
  const suffix = endOfMinute ? ':59.999+05:00' : ':00.000+05:00';
  return new Date(`${normalized}${suffix}`).getTime();
};

const resolveReportRange = (filters, abandonedOnly) => {
  if (!abandonedOnly) {
    if (!filters.startDate || !filters.endDate || filters.startDate > filters.endDate) return null;
    return {
      startDate: filters.startDate,
      endDate: filters.endDate,
      startAt: `${filters.startDate}T00:00`,
      endAt: `${filters.endDate}T23:59`,
      startMs: karachiTimestamp(`${filters.startDate}T00:00`),
      endMs: karachiTimestamp(`${filters.endDate}T23:59`, true)
    };
  }

  if (filters.periodType === 'daily') {
    if (!filters.dailyDate || !filters.dailyDateTo || filters.dailyDate > filters.dailyDateTo) return null;
    return {
      startDate: filters.dailyDate,
      endDate: filters.dailyDateTo,
      startAt: `${filters.dailyDate}T00:00`,
      endAt: `${filters.dailyDateTo}T23:59`,
      startMs: karachiTimestamp(`${filters.dailyDate}T00:00`),
      endMs: karachiTimestamp(`${filters.dailyDateTo}T23:59`, true)
    };
  }

  if (filters.periodType === 'weekly') {
    if (!filters.weekDate) return null;
    const startDate = weekStartIso(filters.weekDate);
    const endDate = addDays(startDate, 6);
    return {
      startDate,
      endDate,
      startAt: `${startDate}T00:00`,
      endAt: `${endDate}T23:59`,
      startMs: karachiTimestamp(`${startDate}T00:00`),
      endMs: karachiTimestamp(`${endDate}T23:59`, true)
    };
  }

  if (filters.periodType === 'monthly') {
    const startDate = filters.month ? `${filters.month}-01` : '';
    const endDate = monthEndIso(filters.month);
    if (!startDate || !endDate) return null;
    return {
      startDate,
      endDate,
      startAt: `${startDate}T00:00`,
      endAt: `${endDate}T23:59`,
      startMs: karachiTimestamp(`${startDate}T00:00`),
      endMs: karachiTimestamp(`${endDate}T23:59`, true)
    };
  }

  if (filters.periodType === 'interval') {
    const startMs = karachiTimestamp(filters.intervalStart);
    const endMs = karachiTimestamp(filters.intervalEnd, true);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs > endMs) return null;
    return {
      startDate: filters.intervalStart.slice(0, 10),
      endDate: filters.intervalEnd.slice(0, 10),
      startAt: filters.intervalStart,
      endAt: filters.intervalEnd,
      startMs,
      endMs
    };
  }

  return null;
};

const formatReportInterval = (range) => range
  ? `${range.startAt.replace('T', ' ')}:00 - ${range.endAt.replace('T', ' ')}:59 (GMT +05:00)`
  : '';

const enumerateDates = (startDate, endDate) => {
  const dates = [];
  const current = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);
  while (current <= end) {
    dates.push(toIsoDate(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

const findArray = (payload, keys) => {
  const roots = [payload, payload?.data, payload?.result].filter(Boolean);
  for (const root of roots) {
    for (const key of keys) {
      if (Array.isArray(root?.[key])) return root[key];
    }
  }
  return [];
};

const parseDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const dateValue = (source) => parseDate(read(source, [
  'CallStartedAt', 'callStartedAt', 'call_started_at',
  'OfferedAt', 'offeredAt', 'offered_at',
  'CreatedAt', 'createdAt', 'created_at'
]));

const formatDateTime = (date) => date
  ? new Intl.DateTimeFormat('en-US', {
    timeZone: 'Asia/Karachi',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date)
  : '-';

const parseSeconds = (value) => {
  const raw = text(value);
  if (!raw) return 0;
  if (/^\d+:\d{1,2}(?::\d{1,2})?$/.test(raw)) {
    return raw.split(':').reduce((total, part) => (total * 60) + Number(part), 0);
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
};

const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(Number(seconds) || 0));
  const hours = String(Math.floor(total / 3600)).padStart(2, '0');
  const minutes = String(Math.floor((total % 3600) / 60)).padStart(2, '0');
  const remainder = String(total % 60).padStart(2, '0');
  return `${hours}:${minutes}:${remainder}`;
};

const normalizeDisposition = (value) => {
  const normalized = text(value).toUpperCase().replace(/[\s-]+/g, '_');
  if (['ANSWER', 'ANSWERED', 'CONNECTED', 'COMPLETE', 'COMPLETED'].includes(normalized)) return 'Answered';
  if (['ABANDON', 'ABANDONED', 'TIMEOUT'].includes(normalized)) return 'Abandoned';
  if (['REJECT', 'REJECTED'].includes(normalized)) return 'Rejected';
  if (['NOT_ANSWERED', 'NOANSWER', 'NO_ANSWER'].includes(normalized)) return 'Not Answered';
  if (['CANCEL', 'CANCELLED', 'CANCELED'].includes(normalized)) return 'Cancelled';
  if (normalized === 'VOICEMAIL') return 'Voicemail';
  if (normalized === 'BUSY') return 'Busy';
  if (['CHANUNAVAIL', 'UNAVAILABLE', 'CONGESTION'].includes(normalized)) return 'Unavailable';
  if (['FAIL', 'FAILED', 'FAILURE'].includes(normalized)) return 'Failed';
  return normalized ? normalized.replace(/_/g, ' ') : 'Unknown';
};

const identityValue = (source, keys) => text(read(source, keys));

const contactAliases = (source = {}) => [
  ['state', identityValue(source, ['CallStateLogId', 'callStateLogId', 'call_state_log_id'])],
  ['uid', identityValue(source, ['CallStateUniqueId', 'callStateUniqueId', 'call_state_unique_id'])],
  ['uid', identityValue(source, ['UniqueId', 'uniqueId', 'unique_id'])],
  ['uid', identityValue(source, ['LinkedId', 'linkedId', 'linked_id'])],
  ['channel', identityValue(source, ['CallStateChannelId', 'callStateChannelId', 'call_state_channel_id'])],
  ['channel', identityValue(source, ['ChannelId', 'channelId', 'channel_id'])]
].filter(([, value]) => Boolean(value)).map(([type, value]) => `${type}:${value}`);

const canonicalContactId = (...sources) => {
  for (const source of sources) {
    for (const keys of [
      ['CallStateUniqueId', 'callStateUniqueId', 'call_state_unique_id'],
      ['UniqueId', 'uniqueId', 'unique_id'],
      ['LinkedId', 'linkedId', 'linked_id']
    ]) {
      const value = identityValue(source, keys);
      if (value) return `uid:${value}`;
    }
  }

  for (const source of sources) {
    const value = identityValue(source, ['CallStateLogId', 'callStateLogId', 'call_state_log_id']);
    if (value) return `state:${value}`;
  }

  return '';
};

const offerPriority = (offer) => {
  const answeredBy = text(read(offer, ['FinalAnsweredBy', 'finalAnsweredBy', 'final_answered_by']));
  const status = normalizeDisposition(read(offer, ['OfferStatus', 'offerStatus', 'offer_status']));
  if (answeredBy) return 3;
  if (status === 'Answered') return 2;
  return text(read(offer, ['AgentExtension', 'agentExtension', 'agent_extension'])) ? 1 : 0;
};

const selectOffer = (offers = []) => [...offers].sort((left, right) => {
  const priorityDifference = offerPriority(right) - offerPriority(left);
  if (priorityDifference) return priorityDifference;
  return (dateValue(right)?.getTime() || 0) - (dateValue(left)?.getTime() || 0);
})[0] || null;

const resolveDelaySeconds = (call, offer, originatedAt) => {
  const directValue = read(call, [
    'QueueHoldTime', 'queueHoldTime', 'queue_hold_time',
    'QueueDuration', 'queueDuration', 'queue_duration',
    'ApplicationDelay', 'applicationDelay', 'application_delay'
  ]);
  if (text(directValue)) return parseSeconds(directValue);

  const offeredAt = dateValue(offer);
  if (originatedAt && offeredAt && offeredAt >= originatedAt) {
    return Math.round((offeredAt.getTime() - originatedAt.getTime()) / 1000);
  }

  return parseSeconds(read(offer, ['RingTimeSeconds', 'ringTimeSeconds', 'ring_time_seconds']));
};

const isTruthy = (value) => ['1', 'true', 'yes', 'y'].includes(text(value).toLowerCase());

const isQueueVoicemail = (call = {}) => [
  read(call, ['CallType', 'callType', 'call_type']),
  read(call, ['CallTypeLabel', 'callTypeLabel', 'call_type_label'])
].some((value) => {
  const normalized = text(value).toLowerCase().replace(/[\s-]+/g, '_');
  return normalized.includes('queue') && normalized.includes('voicemail');
});

const resolveApplication = (call, offer, skillset, queueVoicemail = false) => {
  if (queueVoicemail) return 'All_Queue_Script';

  const explicitHitQueue = read(call, ['HitQueue', 'hitQueue', 'hit_queue']);
  if (isTruthy(explicitHitQueue)) return 'All_Queue_Script';

  const queueEvidence = [
    skillset,
    read(call, ['QueueEnteredAt', 'queueEnteredAt', 'queue_entered_at']),
    read(call, ['QueueName', 'queueName', 'queue_name']),
    read(offer, ['QueueName', 'queueName', 'queue_name', 'Skillset', 'skillset']),
    read(offer, ['AgentExtension', 'agentExtension', 'agent_extension']),
    read(offer, ['CallOfferKey', 'callOfferKey', 'call_offer_key'])
  ].some((value) => Boolean(text(value)));

  return queueEvidence ? 'All_Queue_Script' : 'Master_Script';
};

const resolveIvrDelaySeconds = (call, originatedAt) => {
  const directValue = read(call, [
    'IvrTime', 'ivrTime', 'ivr_time',
    'TotalDuration', 'totalDuration', 'total_duration'
  ]);
  if (text(directValue)) return parseSeconds(directValue);

  const endedAt = parseDate(read(call, [
    'CallEndedAt', 'callEndedAt', 'call_ended_at',
    'EndedAt', 'endedAt', 'ended_at'
  ]));
  if (originatedAt && endedAt && endedAt >= originatedAt) {
    return Math.round((endedAt.getTime() - originatedAt.getTime()) / 1000);
  }
  return 0;
};

const buildRow = (call = {}, offer = {}, index = 0, options = {}) => {
  const aliases = [...contactAliases(call), ...contactAliases(offer)];
  const contactId = canonicalContactId(call, offer) || aliases[0] || `contact-${index}`;
  const originatedAt = dateValue(call) || dateValue(offer);
  const rawDisposition = read(call, ['Disposition', 'disposition', 'QueueStatus', 'queueStatus', 'queue_status']) ||
    read(offer, ['OfferStatus', 'offerStatus', 'offer_status']);
  const queueVoicemail = isQueueVoicemail(call);
  const normalizedDisposition = normalizeDisposition(rawDisposition);
  const disposition = options.queueVoicemailAsAbandoned && queueVoicemail &&
    ['Unknown', 'Voicemail', 'Not Answered'].includes(normalizedDisposition)
    ? 'Abandoned'
    : normalizedDisposition;
  const originator = text(
    read(call, ['CallerNumber', 'callerNumber', 'caller_number']) ||
    read(offer, ['CallerNumber', 'callerNumber', 'caller_number'])
  );
  const address = text(
    read(call, [
      'Address', 'address', 'DnisNumber', 'dnisNumber', 'dnis_number',
      'RoutePointNumber', 'routePointNumber', 'route_point_number',
      'CalledNumber', 'calledNumber', 'called_number'
    ]) ||
    read(offer, ['Address', 'address', 'DnisNumber', 'dnisNumber', 'dnis_number'])
  );
  const skillset = text(
    read(offer, ['QueueName', 'queueName', 'queue_name', 'Skillset', 'skillset']) ||
    read(call, ['QueueName', 'queueName', 'queue_name', 'Skillset', 'skillset'])
  );
  const agentName = text(
    read(offer, ['AgentName', 'agentName', 'agent_name', 'MemberName', 'memberName', 'member_name']) ||
    read(call, ['AgentName', 'agentName', 'agent_name'])
  );
  const agentId = text(
    read(offer, ['FinalAnsweredBy', 'finalAnsweredBy', 'final_answered_by', 'AgentExtension', 'agentExtension', 'agent_extension']) ||
    read(call, ['AnsweredBy', 'answeredBy', 'answered_by', 'QueueAgent', 'queueAgent', 'queue_agent'])
  );
  const explicitTreatment = text(
    read(call, ['LastTreatment', 'lastTreatment', 'last_treatment', 'Treatment', 'treatment']) ||
    read(offer, ['LastTreatment', 'lastTreatment', 'last_treatment', 'Treatment', 'treatment'])
  );
  const lastTreatment = explicitTreatment || (
    agentId ? 'Given Ringback' :
      ['Abandoned', 'Voicemail'].includes(disposition) ? 'Given RAN' : '-'
  );
  const application = resolveApplication(
    call,
    offer,
    skillset,
    options.queueVoicemailAsAbandoned && queueVoicemail
  );
  const queueDelaySeconds = resolveDelaySeconds(call, offer, originatedAt);
  const ivrDelaySeconds = resolveIvrDelaySeconds(call, originatedAt);

  return {
    key: `${contactId}-${originator || index}`,
    contactId,
    originatedAt,
    originator: originator || 'Unknown',
    disposition,
    application,
    contactType: text(read(call, ['ContactType', 'contactType', 'contact_type'])) || 'Voice CDN',
    segment: text(read(call, ['Segment', 'segment'])) || '1',
    site: text(read(call, ['Site', 'site'])) || '1',
    address: address || '-',
    delaySeconds: queueDelaySeconds,
    abandonedDelaySeconds: application === 'All_Queue_Script' ? queueDelaySeconds : ivrDelaySeconds,
    lastTreatment,
    skillset: skillset || '-',
    agent: agentName && agentId ? `${agentName} - ${agentId}` : (agentName || agentId || 'None'),
    handlingSeconds: 0,
    initialDisposition: text(read(call, ['InitialDisposition', 'initialDisposition', 'initial_disposition'])) || 'Local',
    nextAddress: text(read(call, ['NextAddress', 'nextAddress', 'next_address']))
  };
};

const buildRows = (payloads, options = {}) => {
  const calls = payloads.flatMap((payload) => findArray(payload, ['calls', 'callRows', 'call_rows']));
  const offers = payloads.flatMap((payload) => findArray(payload, ['queueOffers', 'queue_offers', 'offers']));
  const offersByAlias = new Map();

  offers.forEach((offer) => {
    contactAliases(offer).forEach((alias) => {
      const entries = offersByAlias.get(alias) || [];
      entries.push(offer);
      offersByAlias.set(alias, entries);
    });
  });

  const usedOffers = new Set();
  const rows = [];
  calls.forEach((call, index) => {
    const direction = text(read(call, ['Direction', 'direction'])).toLowerCase();
    if (direction.includes('outbound')) return;
    const matches = contactAliases(call).flatMap((alias) => offersByAlias.get(alias) || []);
    const offer = selectOffer(Array.from(new Set(matches)));
    matches.forEach((matchingOffer) => usedOffers.add(matchingOffer));
    rows.push(buildRow(call, offer || {}, index, options));
  });

  const unmatchedOfferGroups = new Map();
  offers.forEach((offer, index) => {
    if (usedOffers.has(offer)) return;
    const key = canonicalContactId(offer) || contactAliases(offer)[0] || `offer-${index}`;
    const entries = unmatchedOfferGroups.get(key) || [];
    entries.push(offer);
    unmatchedOfferGroups.set(key, entries);
  });
  Array.from(unmatchedOfferGroups.values()).forEach((entries, index) => {
    rows.push(buildRow({}, selectOffer(entries) || {}, calls.length + index, options));
  });

  const uniqueRows = new Map();
  rows.forEach((row) => {
    const existing = uniqueRows.get(row.contactId);
    if (!existing || (row.originatedAt?.getTime() || 0) > (existing.originatedAt?.getTime() || 0)) {
      uniqueRows.set(row.contactId, row);
    }
  });

  return Array.from(uniqueRows.values()).sort((left, right) =>
    (right.originatedAt?.getTime() || 0) - (left.originatedAt?.getTime() || 0)
  );
};

const OriginatorDispositionReport = ({ abandonedOnly = false }) => {
  const navigate = useNavigate();
  const { user: sessionUser } = useSessionUser();
  const { username, roleName } = useMemo(() => resolveSession(sessionUser), [sessionUser]);
  const [filters, setFilters] = useState(() => {
    const today = todayIso();
    return {
      startDate: monthStartIso(),
      endDate: today,
      disposition: abandonedOnly ? 'Abandoned' : '',
      originator: '',
      periodType: 'daily',
      dailyDate: today,
      dailyDateTo: today,
      weekDate: today,
      month: currentMonthIso(),
      intervalStart: `${today}T00:00`,
      intervalEnd: `${today}T23:59`
    };
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sourceWarning, setSourceWarning] = useState('');
  const [page, setPage] = useState(1);
  const reportRange = useMemo(
    () => resolveReportRange(filters, abandonedOnly),
    [abandonedOnly, filters]
  );

  const loadReport = useCallback(async () => {
    const range = resolveReportRange(filters, abandonedOnly);
    if (!range) {
      setError(abandonedOnly
        ? 'A valid report period is required. From date/time cannot be after To date/time.'
        : 'Start Date and End Date are required, and Start Date cannot be after End Date.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSourceWarning('');
      const dates = enumerateDates(range.startDate, range.endDate);
      const payloads = [];

      for (let index = 0; index < dates.length; index += 5) {
        const batch = dates.slice(index, index + 5);
        const results = await Promise.all(batch.map(async (date) => {
          const params = new URLSearchParams({ from: date, to: date, limit: String(API_LIMIT) });
          if (abandonedOnly) params.set('disposition', 'abandoned');
          const response = await fetch(`${ASTERISK_API_BASE}/call-analytics?${params.toString()}`, {
            headers: authHeaders()
          });
          const responseText = await response.text();
          let payload = null;
          try { payload = responseText ? JSON.parse(responseText) : null; } catch {}
          if (!response.ok) {
            throw new Error(payload?.error || payload?.message || responseText || `Request failed: ${response.status}`);
          }
          return { date, payload: payload || {} };
        }));
        payloads.push(...results);
      }

      const limitedDates = payloads
        .filter(({ payload }) => Number(payload?.count || payload?.data?.count || 0) >= API_LIMIT)
        .map(({ date }) => date);
      const builtRows = buildRows(
        payloads.map(({ payload }) => payload),
        { queueVoicemailAsAbandoned: abandonedOnly }
      );
      setRows(abandonedOnly
        ? builtRows.filter((row) => {
          const timestamp = row.originatedAt?.getTime();
          return Number.isFinite(timestamp) && timestamp >= range.startMs && timestamp <= range.endMs;
        })
        : builtRows);
      setPage(1);
      if (limitedDates.length) {
        setSourceWarning(`Latest ${API_LIMIT} records were returned for: ${limitedDates.join(', ')}.`);
      }
    } catch (err) {
      setRows([]);
      setPage(1);
      setError(err?.message || 'Failed to load Originator By Disposition report.');
    } finally {
      setLoading(false);
    }
  }, [abandonedOnly, filters]);

  useEffect(() => {
    document.body.setAttribute('data-app', 'reports');
    return () => document.body.removeAttribute('data-app');
  }, []);

  useEffect(() => {
    loadReport();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dispositions = useMemo(() => Array.from(new Set(rows.map((row) => row.disposition))).sort(), [rows]);

  const filteredRows = useMemo(() => {
    const originatorFilter = text(filters.originator).toLowerCase();
    const matchingRows = rows.filter((row) => {
      const matchesOriginator = !originatorFilter || row.originator.toLowerCase().includes(originatorFilter);
      const matchesDisposition = abandonedOnly
        ? row.disposition === 'Abandoned'
        : (!filters.disposition || row.disposition === filters.disposition);
      return matchesOriginator && matchesDisposition;
    });

    if (!abandonedOnly) return matchingRows;
    const applicationOrder = { All_Queue_Script: 0, Master_Script: 1 };
    return [...matchingRows].sort((left, right) => {
      const applicationDifference =
        (applicationOrder[left.application] ?? 99) - (applicationOrder[right.application] ?? 99);
      if (applicationDifference) return applicationDifference;
      const originatorDifference = left.originator.localeCompare(right.originator, undefined, { numeric: true });
      if (originatorDifference) return originatorDifference;
      return (left.originatedAt?.getTime() || 0) - (right.originatedAt?.getTime() || 0);
    });
  }, [abandonedOnly, filters.disposition, filters.originator, rows]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const visibleRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const groups = useMemo(() => {
    const dispositionGroups = new Map();
    visibleRows.forEach((row) => {
      if (!dispositionGroups.has(row.disposition)) dispositionGroups.set(row.disposition, new Map());
      const originators = dispositionGroups.get(row.disposition);
      if (!originators.has(row.originator)) originators.set(row.originator, []);
      originators.get(row.originator).push(row);
    });
    return Array.from(dispositionGroups.entries()).map(([disposition, originators]) => {
      const dispositionRows = Array.from(originators.values()).flat();
      const averageDelay = dispositionRows.length
        ? dispositionRows.reduce((sum, row) => sum + row.delaySeconds, 0) / dispositionRows.length
        : 0;
      return {
        disposition,
        total: dispositionRows.length,
        averageDelay,
        originators: Array.from(originators.entries())
      };
    });
  }, [visibleRows]);

  const abandonedGroups = useMemo(() => {
    if (!abandonedOnly) return [];

    const fullRowsByApplication = new Map();
    filteredRows.forEach((row) => {
      const application = row.application || 'Master_Script';
      const entries = fullRowsByApplication.get(application) || [];
      entries.push(row);
      fullRowsByApplication.set(application, entries);
    });

    const visibleRowsByApplication = new Map();
    visibleRows.forEach((row) => {
      const application = row.application || 'Master_Script';
      const entries = visibleRowsByApplication.get(application) || [];
      entries.push(row);
      visibleRowsByApplication.set(application, entries);
    });

    return ['All_Queue_Script', 'Master_Script']
      .filter((application) => visibleRowsByApplication.has(application))
      .map((application) => {
        const allApplicationRows = fullRowsByApplication.get(application) || [];
        const originators = new Map();
        (visibleRowsByApplication.get(application) || []).forEach((row) => {
          const entries = originators.get(row.originator) || [];
          entries.push(row);
          originators.set(row.originator, entries);
        });
        const averageDelay = allApplicationRows.length
          ? allApplicationRows.reduce((sum, row) => sum + row.abandonedDelaySeconds, 0) / allApplicationRows.length
          : 0;
        return {
          application,
          total: allApplicationRows.length,
          averageDelay,
          originators: Array.from(originators.entries())
        };
      });
  }, [abandonedOnly, filteredRows, visibleRows]);

  const updateField = (event) => {
    const { name, value } = event.target;
    setFilters((previous) => ({ ...previous, [name]: value.trimStart() }));
    setPage(1);
  };

  const exportExcel = () => {
    if (abandonedOnly) {
      const exportRows = [
        ['Originator By Disposition'],
        ['Report Interval', formatReportInterval(reportRange)],
        ['Site Name', 'CC'],
        [],
        [
          'Application', 'Disposition', 'Originator', 'Originated Time', 'Contact Type',
          'Contact ID', 'Segment', 'Site', 'Address', 'Application Delay', 'Last Treatment',
          'Skillset', 'Agent Name - ID', 'Handling Time', 'Initial Disposition', 'Next Address'
        ],
        ...filteredRows.map((row) => [
          row.application,
          'Abandoned',
          row.originator,
          formatDateTime(row.originatedAt),
          row.contactType,
          row.contactId,
          row.segment,
          row.site,
          row.address,
          formatDuration(row.abandonedDelaySeconds),
          row.lastTreatment,
          row.skillset,
          row.agent,
          formatDuration(row.handlingSeconds),
          row.initialDisposition,
          row.nextAddress
        ])
      ];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(exportRows), 'Abandoned Report');
      XLSX.writeFile(
        workbook,
        `abandoned-${filters.periodType}-${reportRange?.startDate || 'from'}-${reportRange?.endDate || 'to'}.xlsx`
      );
      return;
    }

    const exportRows = filteredRows.map((row) => ({
      Disposition: row.disposition,
      Originator: row.originator,
      'Originated Time': formatDateTime(row.originatedAt),
      'Contact ID': row.contactId,
      Address: row.address,
      'Application Delay': formatDuration(row.delaySeconds),
      'Last Treatment': row.lastTreatment,
      Skillset: row.skillset,
      'Agent Name - ID': row.agent
    }));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(exportRows), 'Originator Disposition');
    XLSX.writeFile(workbook, `originator-by-disposition-${filters.startDate}-${filters.endDate}.xlsx`);
  };

  return (
    <div className="cdn-report-shell originator-report-shell">
      <header className="cdn-report-topbar no-print">
        <div>
          <strong>SLACC</strong>
          <span>Sindh Legal Advisory Call Center</span>
        </div>
        <div>
          <h1>{abandonedOnly ? 'Abandoned Report' : 'Originator By Disposition'}</h1>
          <p>{abandonedOnly ? 'Historical inbound abandoned calls' : 'Asterisk inbound queue report'}</p>
        </div>
        <div className="cdn-report-user-actions">
          <span>{username ? `${username}${roleName ? ` - ${roleName}` : ''}` : 'Report Module'}</span>
          <button type="button" className="cdn-btn soft" onClick={() => navigate('/backoffice')}>Back Office</button>
          <button type="button" className="cdn-btn danger" onClick={() => navigate('/logout')}>Logout</button>
        </div>
      </header>

      <section className="cdn-filter-panel no-print">
        <div className="originator-filter-grid">
          {abandonedOnly ? (
            <div className="abandoned-period-selector" role="tablist" aria-label="Abandoned report period">
              {REPORT_PERIODS.map((period) => (
                <button
                  key={period.key}
                  type="button"
                  role="tab"
                  aria-selected={filters.periodType === period.key}
                  className={`cdn-btn ${filters.periodType === period.key ? 'primary' : 'soft'}`}
                  onClick={() => setFilters((previous) => ({ ...previous, periodType: period.key }))}
                >
                  {period.label}
                </button>
              ))}
            </div>
          ) : null}
          {!abandonedOnly ? (
            <>
              <label><span>Start Date</span><input type="date" name="startDate" value={filters.startDate} onChange={updateField} /></label>
              <label><span>End Date</span><input type="date" name="endDate" value={filters.endDate} onChange={updateField} /></label>
              <label>
                <span>Disposition</span>
                <select name="disposition" value={filters.disposition} onChange={updateField}>
                  <option value="">All</option>
                  {dispositions.map((disposition) => <option key={disposition} value={disposition}>{disposition}</option>)}
                </select>
              </label>
            </>
          ) : null}
          {abandonedOnly && filters.periodType === 'daily' ? (
            <>
              <label><span>Date From</span><input type="date" name="dailyDate" value={filters.dailyDate} onChange={updateField} /></label>
              <label><span>Date To</span><input type="date" name="dailyDateTo" value={filters.dailyDateTo} onChange={updateField} /></label>
            </>
          ) : null}
          {abandonedOnly && filters.periodType === 'weekly' ? (
            <label><span>Week Containing</span><input type="date" name="weekDate" value={filters.weekDate} onChange={updateField} /></label>
          ) : null}
          {abandonedOnly && filters.periodType === 'monthly' ? (
            <label><span>Month</span><input type="month" name="month" value={filters.month} onChange={updateField} /></label>
          ) : null}
          {abandonedOnly && filters.periodType === 'interval' ? (
            <>
              <label><span>From</span><input type="datetime-local" name="intervalStart" value={filters.intervalStart} onChange={updateField} /></label>
              <label><span>To</span><input type="datetime-local" name="intervalEnd" value={filters.intervalEnd} onChange={updateField} /></label>
            </>
          ) : null}
          <label><span>Originator</span><input type="text" name="originator" value={filters.originator} onChange={updateField} placeholder="Caller number" /></label>
          <div className="cdn-filter-actions">
            <button type="button" className="cdn-btn primary" onClick={loadReport} disabled={loading}>{loading ? 'Loading' : 'Show'}</button>
            <button type="button" className="cdn-btn success" onClick={exportExcel} disabled={!filteredRows.length}>Excel</button>
            <button type="button" className="cdn-btn soft" onClick={() => window.print()} disabled={!filteredRows.length}>PDF</button>
          </div>
        </div>
        {error ? <div className="cdn-alert">{error}</div> : null}
        {sourceWarning ? <div className="dnis-data-issues"><p>{sourceWarning}</p></div> : null}
      </section>

      <main className="cdn-report-paper originator-report-paper">
        <section className="cdn-report-heading">
          <h2>{abandonedOnly ? 'Abandoned Report' : 'Originator By Disposition'}</h2>
          <p>
            <strong>Report Interval:</strong>{' '}
            {abandonedOnly
              ? (reportRange ? formatReportInterval(reportRange) : 'Select a valid report period')
              : `${filters.startDate} 00:00:00 - ${filters.endDate} 23:59:59 (GMT +05:00)`}
          </p>
        </section>

        <div className="originator-report-scroll">
          <div className={`originator-report-content ${abandonedOnly ? 'abandoned-report-content' : ''}`}>
            <div className={`originator-column-headings ${abandonedOnly ? 'abandoned-originator-grid' : ''}`}>
              <span>Originated Time</span>
              {abandonedOnly ? <span>Contact Type</span> : null}
              <span>Contact ID</span>
              {abandonedOnly ? <span>Segment</span> : null}
              {abandonedOnly ? <span>Site</span> : null}
              <span>Address</span>
              <span>Application Delay</span>
              <span>Last Treatment</span>
              <span>Skillset</span>
              <span>Agent Name - ID</span>
              {abandonedOnly ? <span>Handling Time</span> : null}
              {abandonedOnly ? <span>Initial Disposition</span> : null}
              {abandonedOnly ? <span>Next Address</span> : null}
            </div>

            {abandonedOnly ? (
              abandonedGroups.length ? abandonedGroups.map((group) => (
                <React.Fragment key={group.application}>
                  <section className="originator-application-summary">
                    <strong>Application: {group.application}</strong>
                    <span>Total Contacts: {group.total}</span>
                  </section>
                  <section className="originator-disposition-section">
                    <div className="originator-disposition-summary">
                      <div><strong>Disposition: Abandoned</strong><span>Total Contacts: {group.total}</span></div>
                      <span>Average Delay: {group.averageDelay.toFixed(2)}</span>
                    </div>
                    {group.originators.map(([originator, originatorRows]) => (
                      <div className="originator-contact-group" key={`${group.application}-${originator}`}>
                        <h3>Originator: {originator}</h3>
                        {originatorRows.map((row) => (
                          <div className="originator-data-row abandoned-originator-grid" key={row.key}>
                            <span>{formatDateTime(row.originatedAt)}</span>
                            <span>{row.contactType}</span>
                            <span>{row.contactId}</span>
                            <span>{row.segment}</span>
                            <span>{row.site}</span>
                            <span>{row.address}</span>
                            <span>{formatDuration(row.abandonedDelaySeconds)}</span>
                            <span>{row.lastTreatment}</span>
                            <span>{row.skillset}</span>
                            <span>{row.agent}</span>
                            <span>{formatDuration(row.handlingSeconds)}</span>
                            <span>{row.initialDisposition}</span>
                            <span>{row.nextAddress}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </section>
                </React.Fragment>
              )) : <div className="cdn-empty">{loading ? 'Loading contacts...' : 'No abandoned contacts found.'}</div>
            ) : (
              <>
                <section className="originator-application-summary">
                  <strong>Application: Asterisk Queue</strong>
                  <span>Total Contacts: {filteredRows.length}</span>
                </section>
                {groups.length ? groups.map((group) => (
                  <section className="originator-disposition-section" key={group.disposition}>
                    <div className="originator-disposition-summary">
                      <div><strong>Disposition: {group.disposition}</strong><span>Total Contacts: {group.total}</span></div>
                      <span>Average Delay: {formatDuration(group.averageDelay)}</span>
                    </div>
                    {group.originators.map(([originator, originatorRows]) => (
                      <div className="originator-contact-group" key={`${group.disposition}-${originator}`}>
                        <h3>Originator: {originator}</h3>
                        {originatorRows.map((row) => (
                          <div className="originator-data-row" key={row.key}>
                            <span>{formatDateTime(row.originatedAt)}</span>
                            <span>{row.contactId}</span>
                            <span>{row.address}</span>
                            <span>{formatDuration(row.delaySeconds)}</span>
                            <span>{row.lastTreatment}</span>
                            <span>{row.skillset}</span>
                            <span>{row.agent}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </section>
                )) : <div className="cdn-empty">{loading ? 'Loading contacts...' : 'No contacts found.'}</div>}
              </>
            )}
          </div>
        </div>
      </main>

      <div className="cdn-pager no-print">
        <button type="button" className="cdn-btn soft" disabled={page <= 1} onClick={() => setPage((current) => current - 1)}>Previous</button>
        <span>Page {page} of {totalPages} ({filteredRows.length} contacts)</span>
        <button type="button" className="cdn-btn soft" disabled={page >= totalPages} onClick={() => setPage((current) => current + 1)}>Next</button>
      </div>
    </div>
  );
};

export default OriginatorDispositionReport;
